import React, { useState, useEffect, useRef } from "react";
import { ChevronDown } from "lucide-react";
import { useLang } from "../lib/providers";

/**
 * Wraps any homepage section so it appears as a click-to-expand panel.
 * The title is shown collapsed by default; clicking expands the children.
 *
 * If the URL hash matches the section id (e.g. #about), the panel auto-opens
 * AND scrolls smoothly into view — so navbar links like <a href="#about">
 * keep working even when the section is collapsed.
 */
export default function CollapsibleSection({ id, titleFr, titleAr, defaultOpen = false, children }) {
  const { lang } = useLang();
  const [open, setOpen] = useState(defaultOpen);
  const ref = useRef(null);
  const title = lang === "ar" ? (titleAr || titleFr) : (titleFr || titleAr);

  // Auto-open + scroll when the URL hash points to this section.
  // We do NOT put id={id} on the wrapper to avoid colliding with the inner
  // section id (e.g. About.jsx already has id="about"). Instead, on hashchange
  // we open the panel; a separate effect (depending on `open`) handles the
  // actual scroll once the panel has finished mounting its children.
  const [pendingScroll, setPendingScroll] = useState(false);

  useEffect(() => {
    const handleHash = () => {
      const hash = (window.location.hash || "").replace("#", "");
      if (hash !== id) return;
      setOpen(true);
      setPendingScroll(true);
    };
    handleHash(); // on mount
    window.addEventListener("hashchange", handleHash);
    return () => window.removeEventListener("hashchange", handleHash);
  }, [id]);

  // After the panel has expanded (open && pendingScroll), scroll to the section.
  // We retry a few times because lazy-loaded images can change layout.
  useEffect(() => {
    if (!pendingScroll || !open) return;
    const doScroll = (smooth = false) => {
      const target = document.getElementById(id) || ref.current;
      if (!target) return;
      const rect = target.getBoundingClientRect();
      const top = rect.top + window.pageYOffset - 80;
      window.scrollTo({ top, behavior: smooth ? "smooth" : "auto" });
    };
    // Quick "auto" snap first (in case panel just opened) — then one smooth
    // re-scroll after lazy-loaded images settle the layout.
    const timers = [
      setTimeout(() => doScroll(false), 100),
      setTimeout(() => doScroll(false), 600),
      setTimeout(() => { doScroll(true); setPendingScroll(false); }, 1500),
    ];
    return () => timers.forEach(clearTimeout);
  }, [open, pendingScroll, id]);

  return (
    <section ref={ref} className="py-6 sm:py-8 border-b border-white/5" data-testid={`collapsible-${id}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-3 py-2 text-left group"
          aria-expanded={open}
          data-testid={`collapsible-toggle-${id}`}
        >
          <h2 className="font-heading font-black uppercase tracking-tight text-2xl sm:text-3xl group-hover:text-neon transition-colors">
            {title}
          </h2>
          <ChevronDown
            className={`w-6 h-6 text-white/60 group-hover:text-neon transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
        {open && (
          <div className="mt-2 animate-in fade-in slide-in-from-top-2 duration-300" data-testid={`collapsible-body-${id}`}>
            {children}
          </div>
        )}
      </div>
    </section>
  );
}
